import { getFestivals, showFestival } from '../api/festivalApi';
import { getGuides, showGuide } from '../api/guideApi';
import { getJourneys, showJourney } from '../api/journeyApi';
import { showDestination } from '../api/destinationApi';
import { getAllFestivals, getFestivalBySlug } from '../data/festivalCatalog';
import {
	getAllStaticGuides, getStaticGuideBySlug, getStaticGuidesByCategory,
} from '../data/guideCatalog';
import { resolveGuideSlug } from '../data/guideSlugAliases';
import {
	getStaticDestinationBySlug,
} from '../data/destinationCatalog';
import { JOURNEY_REGISTRY, getJourneyBySlug } from '../data/journeys';
import { normalizeApiJourney, normalizeJourneySummary, journeySummaryFromStatic } from './journeyNormalizer';

export function normalizeListPayload(data, rootKey) {
	if (Array.isArray(data)) return data;
	if (data && Array.isArray(data[rootKey])) return data[rootKey];
	return [];
}

/** API-first festival list — static catalog only if API is empty or unavailable. */
export async function loadFestivalsApiFirst(params = {}) {
	try {
		const res = await getFestivals(params);
		const apiFestivals = normalizeListPayload(res.data, 'festivals');
		if (apiFestivals.length > 0) {
			return apiFestivals.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
		}
	} catch {
		// fall through to static
	}
	const staticFestivals = getAllFestivals();
	if (params.district_id) {
		return staticFestivals.filter((f) => f.district_id === params.district_id);
	}
	return staticFestivals;
}

/** API-first festival detail — static fallback for resilience. */
export async function loadFestivalBySlug(slug) {
	try {
		const res = await showFestival(slug, { skipGlobalError: true });
		const festival = res.data?.festival || res.data;
		if (festival && festival.slug) return festival;
	} catch {
		// fall through
	}
	return getFestivalBySlug(slug) || null;
}

/** API-first guides by category — static seed catalog if API empty. */
export async function loadGuidesByCategory(category) {
	try {
		const res = await getGuides({ category });
		const guides = normalizeListPayload(res.data, 'guides');
		if (guides.length > 0) return guides;
	} catch {
		// fall through
	}
	return getStaticGuidesByCategory(category);
}

/** API-first guide list — static seed catalog if API empty. */
export async function loadGuidesListApiFirst(params = {}) {
	try {
		const res = await getGuides({ per_page: 200, ...params });
		const guides = normalizeListPayload(res.data, 'guides');
		if (guides.length > 0) return guides;
	} catch {
		// fall through
	}
	let guides = getAllStaticGuides();
	if (params.category) {
		guides = guides.filter((g) => g.category === params.category);
	}
	return guides;
}

/** Load a single published guide — API first, static seed fallback, slug alias aware. */
export async function loadGuideBySlug(slug) {
	const canonicalSlug = resolveGuideSlug(slug);
	try {
		const res = await showGuide(canonicalSlug, { skipGlobalError: true });
		const guide = res.data?.guide || res.data;
		if (guide && guide.slug) return guide;
	} catch {
		// fall through
	}
	return getStaticGuideBySlug(canonicalSlug) || null;
}

/** Load a single destination — API first, static seed fallback. */
export async function loadDestinationBySlug(slug) {
	try {
		const res = await showDestination(slug, { skipGlobalError: true });
		const destination = res.data?.destination || res.data;
		if (destination && destination.slug) return destination;
	} catch {
		// fall through
	}
	return getStaticDestinationBySlug(slug) || null;
}

/** Split plain-text CMS body into paragraphs for rendering. */
export function splitBodyParagraphs(text) {
	if (!text) return [];
	return text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
}

/** Extract normalized pagination meta from a journeys API response. */
export function extractJourneyPaginationMeta(data) {
	const pag = data?.meta?.pagination || {};
	const totalCount = pag.total_count ?? pag.total_entries ?? 0;
	return {
		per_page: pag.per_page,
		current_page: pag.current_page || 1,
		total_pages: pag.total_pages || 1,
		total_count: totalCount,
		total_entries: pag.total_entries ?? totalCount,
	};
}

function staticJourneyFallback(params = {}) {
	let list = JOURNEY_REGISTRY.map((journey, index) => journeySummaryFromStatic(journey, index));
	if (params.region) list = list.filter((j) => j.region === params.region);
	if (params.featured === true || params.featured === 'true') {
		const featuredSlugs = new Set(['everest-base-camp', 'annapurna-base-camp', 'langtang-valley']);
		list = list.filter((j) => featuredSlugs.has(j.slug));
	}
	const search = (params.search || params.q?.title_cont || '').trim().toLowerCase();
	if (search) {
		list = list.filter((j) => [j.title, j.subtitle, j.lead, j.region, j.slug]
			.some((s) => s && String(s).toLowerCase().includes(search)));
	}
	list = list.sort((a, b) => (a.priority || 0) - (b.priority || 0) || a.title.localeCompare(b.title));
	const perPage = Number(params.per_page) || list.length || 1;
	const page = Number(params.page) || 1;
	const totalCount = list.length;
	const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
	const start = (page - 1) * perPage;
	const journeys = list.slice(start, start + perPage);
	return {
		journeys,
		meta: {
			per_page: perPage,
			current_page: page,
			total_pages: totalPages,
			total_count: totalCount,
			total_entries: totalCount,
		},
	};
}

/**
 * Paginated journey list — returns { journeys, meta }.
 * Use meta.total_count for published totals; never infer counts from array length alone.
 */
export async function loadJourneysPage(params = {}, { staticFallback = true } = {}) {
	try {
		const res = await getJourneys(params);
		const apiJourneys = normalizeListPayload(res.data, 'journeys');
		const meta = extractJourneyPaginationMeta(res.data);
		if (apiJourneys.length > 0 || meta.total_count > 0) {
			return {
				journeys: apiJourneys.map((row) => normalizeJourneySummary(row)),
				meta,
			};
		}
		if (!staticFallback) {
			return { journeys: [], meta: { ...meta, total_count: 0, total_pages: 1, current_page: 1 } };
		}
	} catch {
		if (!staticFallback) {
			return { journeys: [], meta: { total_count: 0, total_pages: 1, current_page: 1, per_page: params.per_page || 10 } };
		}
	}
	return staticJourneyFallback(params);
}

/** Lightweight published journey count — one row fetch, meta carries total_count. */
export async function fetchPublishedJourneyCount() {
	const { meta } = await loadJourneysPage({ per_page: 1 }, { staticFallback: true });
	return meta.total_count || 0;
}

/** API-first journey list — static registry only if API is empty or unavailable. */
export async function loadJourneysApiFirst(params = {}) {
	const { journeys } = await loadJourneysPage(params);
	return journeys;
}

/** API-first journey detail — static fallback for resilience. */
export async function loadJourneyBySlug(slug) {
	try {
		const res = await showJourney(slug, { skipGlobalError: true });
		const row = res.data?.journey || res.data;
		const normalized = normalizeApiJourney(row);
		if (normalized) return normalized;
	} catch {
		// fall through
	}
	return getJourneyBySlug(slug) || null;
}
